'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { selectPreferredTenant } from '@/src/auth/tenant-selection';
import { getBearerToken } from '@/src/auth/token-storage';
import { logger } from '@/src/logging/logger';
import { BrowserVoiceRuntimeProvider } from '../voice/browser-voice-runtime';
import { reconcileVoiceEndpointIdentity } from '../voice/types';

import { listContactCenterRealtimeEvents } from '../api';
import { createContactCenterRealtimeSocket } from './contact-center-realtime-client';
import { ContactCenterRealtimeEvent } from './contact-center-realtime-types';
import type {
  ContactCenterBrowserRegistrationChangedPayload,
  ContactCenterRealtimeCatchUpEvent,
  ContactCenterRealtimeConnectionStatus,
  ContactCenterRealtimeInboxPayload,
  ContactCenterRealtimePayload,
  ContactCenterRealtimeReason,
  ContactCenterVoiceCallChangedPayload,
  EmployeeVoiceChangedPayload,
} from './contact-center-realtime-types';

const conversationEvents = [
  ContactCenterRealtimeEvent.ConversationChanged,
  ContactCenterRealtimeEvent.MessageCreated,
  ContactCenterRealtimeEvent.DeliveryUpdated,
  ContactCenterRealtimeEvent.AssignmentChanged,
  ContactCenterRealtimeEvent.CrmLinksChanged,
  ContactCenterRealtimeEvent.SlaChanged,
] as const;

const fallbackRefreshMs = 30_000;
const maxFallbackRefreshes = 20;
const maxAuthRecoveryAttempts = 1;
const catchUpPageSize = 100;
const recoverableAuthErrorCodes = new Set([
  'TENANT_UNAVAILABLE',
  'TOKEN_INVALID',
]);
const realtimeReasons = new Set<string>([
  'message_created',
  'delivery_updated',
  'assignment_changed',
  'crm_links_changed',
  'lifecycle_changed',
  'connector_changed',
  'sla_changed',
]);

type RefreshReason = 'event' | 'reconnect_catch_up' | 'fallback';

type Subscriber = {
  id: number;
  conversationId?: string;
  onRefresh: (payload?: ContactCenterRealtimePayload) => void | Promise<void>;
  pendingPayload?: ContactCenterRealtimePayload;
  refreshTimeout: number | null;
};

type VoiceSubscriber = {
  id: number;
  onCallChanged?: (payload: ContactCenterVoiceCallChangedPayload) => void;
  onBrowserRegistrationChanged?: (
    payload: ContactCenterBrowserRegistrationChangedPayload,
  ) => void;
  onEmployeeVoiceChanged?: (payload: EmployeeVoiceChangedPayload) => void;
  onRecover?: () => void | Promise<void>;
  includeOrganization: boolean;
};

type ContactCenterRealtimeState = {
  status: ContactCenterRealtimeConnectionStatus;
  reason: string | null;
};

type ContactCenterRealtimeContextValue = ContactCenterRealtimeState & {
  subscribe: (subscriber: Pick<Subscriber, 'conversationId' | 'onRefresh'>) => () => void;
  subscribeVoice: (
    subscriber: Pick<
      VoiceSubscriber,
      | 'onCallChanged'
      | 'onBrowserRegistrationChanged'
      | 'onEmployeeVoiceChanged'
      | 'onRecover'
    > & { includeOrganization?: boolean },
  ) => () => void;
  getLatestVoiceCall: (connectorId: string, clientUserRef: string) => ContactCenterVoiceCallChangedPayload | null;
  getVoiceCalls: (connectorId: string, clientUserRef: string) => ContactCenterVoiceCallChangedPayload[];
};

type ContactCenterConnectError = Error & {
  data?: { code?: unknown; reason?: unknown };
};

const connectedState: ContactCenterRealtimeState = { status: 'connected', reason: null };
const ContactCenterRealtimeContext = createContext<ContactCenterRealtimeContextValue | null>(null);

function errorReason(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

function connectErrorCode(error: Error): string | null {
  const data = (error as ContactCenterConnectError).data;
  return typeof data?.code === 'string' ? data.code : null;
}

function apiErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message) {
      return message;
    }
  }
  return fallback;
}

function toRealtimePayload(event: ContactCenterRealtimeCatchUpEvent): ContactCenterRealtimePayload | null {
  if (!realtimeReasons.has(event.reason)) {
    return null;
  }

  return {
    cursor: event.cursor,
    tenantId: event.tenantId,
    conversationId: event.conversationId,
    ...(event.connectorId ? { connectorId: event.connectorId } : {}),
    ...(event.messageId ? { messageId: event.messageId } : {}),
    reason: event.reason as ContactCenterRealtimeReason,
    occurredAt: event.occurredAt,
  };
}

function setSocketAuth(socket: ReturnType<typeof createContactCenterRealtimeSocket>, token: string | null): void {
  socket.auth = { token };
}

function socketTransport(socket: ReturnType<typeof createContactCenterRealtimeSocket>): string {
  return socket.io.engine?.transport?.name ?? 'unknown';
}

export function shouldAdoptVoiceCall(
  current: ContactCenterVoiceCallChangedPayload | undefined,
  incoming: ContactCenterVoiceCallChangedPayload,
): boolean {
  if (!current) return true;
  if (current.callSessionId === incoming.callSessionId) {
    return incoming.stateSequence > current.stateSequence;
  }
  const currentAt = Date.parse(current.lastEventAt);
  const incomingAt = Date.parse(incoming.lastEventAt);
  return !Number.isNaN(incomingAt) &&
    (Number.isNaN(currentAt) || incomingAt > currentAt);
}

export function reconcileLatestVoiceCall(
  current: ContactCenterVoiceCallChangedPayload | undefined,
  incoming: ContactCenterVoiceCallChangedPayload,
): ContactCenterVoiceCallChangedPayload {
  if (!current || current.callSessionId !== incoming.callSessionId) return incoming;
  return {
    ...incoming,
    callerName: incoming.callerName ?? current.callerName ?? null,
    ...reconcileVoiceEndpointIdentity(current, incoming, true),
  };
}

export function ContactCenterRealtimeProvider({
  children,
  persistentBrowserVoice = false,
}: {
  children: ReactNode;
  persistentBrowserVoice?: boolean;
}) {
  const [state, setState] = useState<ContactCenterRealtimeState>(connectedState);
  const stateRef = useRef<ContactCenterRealtimeState>(connectedState);
  const socketRef = useRef<ReturnType<typeof createContactCenterRealtimeSocket> | null>(null);
  const subscribersRef = useRef(new Map<number, Subscriber>());
  const nextSubscriberIdRef = useRef(1);
  const voiceSubscribersRef = useRef(new Map<number, VoiceSubscriber>());
  const nextVoiceSubscriberIdRef = useRef(1);
  const latestVoiceCallsRef = useRef(new Map<string, ContactCenterVoiceCallChangedPayload>());
  const latestBrowserRegistrationVersionsRef = useRef(new Map<string, number>());
  const fallbackIntervalRef = useRef<number | null>(null);
  const fallbackRefreshCountRef = useRef(0);
  const hasConnectedRef = useRef(false);
  const lastCursorRef = useRef<string | null>(null);
  const authRecoveryAttemptsRef = useRef(0);
  const authRecoveryTimeoutRef = useRef<number | null>(null);
  const disconnectTimeoutRef = useRef<number | null>(null);
  const connectingRef = useRef(false);
  const intentionalDisconnectRef = useRef(false);
  const authTerminatedRef = useRef(false);

  const updateState = useCallback((next: ContactCenterRealtimeState) => {
    stateRef.current = next;
    setState(next);
  }, []);

  const setRealtimeReason = useCallback((reason: string | null) => {
    updateState({ status: stateRef.current.status, reason });
  }, [updateState]);

  const clearAuthRecoveryTimeout = useCallback(() => {
    if (authRecoveryTimeoutRef.current !== null) {
      window.clearTimeout(authRecoveryTimeoutRef.current);
      authRecoveryTimeoutRef.current = null;
    }
  }, []);

  const clearDisconnectTimeout = useCallback(() => {
    if (disconnectTimeoutRef.current !== null) {
      window.clearTimeout(disconnectTimeoutRef.current);
      disconnectTimeoutRef.current = null;
    }
  }, []);

  const stopFallbackRefresh = useCallback(() => {
    if (fallbackIntervalRef.current !== null) {
      window.clearInterval(fallbackIntervalRef.current);
      fallbackIntervalRef.current = null;
    }
    fallbackRefreshCountRef.current = 0;
  }, []);

  const disconnectWhenIdle = useCallback(() => {
    clearDisconnectTimeout();
    disconnectTimeoutRef.current = window.setTimeout(() => {
      disconnectTimeoutRef.current = null;
      if (
        subscribersRef.current.size > 0 ||
        voiceSubscribersRef.current.size > 0
      ) {
        return;
      }

      clearAuthRecoveryTimeout();
      stopFallbackRefresh();
      hasConnectedRef.current = false;
      authRecoveryAttemptsRef.current = 0;
      intentionalDisconnectRef.current = true;
      socketRef.current?.disconnect();
      updateState(connectedState);
    }, 0);
  }, [clearAuthRecoveryTimeout, clearDisconnectTimeout, stopFallbackRefresh, updateState]);

  const runRefresh = useCallback((subscriber: Subscriber, payload: ContactCenterRealtimePayload | undefined, reason: RefreshReason) => {
    Promise.resolve(subscriber.onRefresh(payload)).catch((error: unknown) => {
      logger.error('Contact Center realtime refresh failed', error, {
        reason,
        conversationId: payload?.conversationId ?? subscriber.conversationId ?? null,
      });
    });
  }, []);

  const scheduleRefresh = useCallback((subscriber: Subscriber, payload: ContactCenterRealtimePayload | undefined, reason: RefreshReason) => {
    subscriber.pendingPayload = payload;
    if (subscriber.refreshTimeout !== null) {
      window.clearTimeout(subscriber.refreshTimeout);
    }
    subscriber.refreshTimeout = window.setTimeout(() => {
      subscriber.refreshTimeout = null;
      const pendingPayload = subscriber.pendingPayload;
      subscriber.pendingPayload = undefined;
      runRefresh(subscriber, pendingPayload, reason);
    }, 100);
  }, [runRefresh]);

  const refreshSubscribers = useCallback((payload: ContactCenterRealtimePayload | undefined, reason: RefreshReason) => {
    for (const subscriber of subscribersRef.current.values()) {
      if (payload?.conversationId && subscriber.conversationId && payload.conversationId !== subscriber.conversationId) {
        continue;
      }
      scheduleRefresh(subscriber, payload, reason);
    }
  }, [scheduleRefresh]);

  const recoverVoiceSubscribers = useCallback((reason: RefreshReason) => {
    for (const subscriber of voiceSubscribersRef.current.values()) {
      Promise.resolve(subscriber.onRecover?.()).catch((error: unknown) => {
        logger.warn('Voice realtime recovery failed', {
          reason,
          error: error instanceof Error ? error.message : String(error),
        });
      });
    }
  }, []);

  const startFallbackRefresh = useCallback(() => {
    if (authTerminatedRef.current) return;
    if (fallbackIntervalRef.current !== null) {
      return;
    }

    fallbackIntervalRef.current = window.setInterval(() => {
      fallbackRefreshCountRef.current += 1;
      if (fallbackRefreshCountRef.current > maxFallbackRefreshes) {
        stopFallbackRefresh();
        updateState({ status: 'degraded', reason: 'Realtime fallback refresh limit reached' });
        logger.warn('Contact Center realtime fallback refresh limit reached');
        return;
      }

      refreshSubscribers(undefined, 'fallback');
      recoverVoiceSubscribers('fallback');
    }, fallbackRefreshMs);
  }, [recoverVoiceSubscribers, refreshSubscribers, stopFallbackRefresh, updateState]);

  const activeInboxSubscribers = useCallback(() => {
    for (const subscriber of subscribersRef.current.values()) {
      if (!subscriber.conversationId) {
        return true;
      }
    }
    return false;
  }, []);

  const activeConversationIds = useCallback(() => {
    const conversationIds = new Set<string>();
    for (const subscriber of subscribersRef.current.values()) {
      if (subscriber.conversationId) {
        conversationIds.add(subscriber.conversationId);
      }
    }
    return conversationIds;
  }, []);

  const hasOrganizationVoiceSubscriber = useCallback(() => {
    for (const subscriber of voiceSubscribersRef.current.values()) {
      if (subscriber.includeOrganization) return true;
    }
    return false;
  }, []);

  const syncRooms = useCallback(() => {
    const socket = socketRef.current;
    if (!socket?.connected) {
      return;
    }

    if (activeInboxSubscribers()) {
      socket.emit('contact_center.inbox.subscribe', (response?: { ok?: boolean }) => {
        if (response && response.ok === false) {
          setRealtimeReason('Inbox realtime room join was rejected');
        }
      });
    }

    socket.emit('contact_center.voice.subscribe', (response?: { ok?: boolean }) => {
      if (response && response.ok === false) {
        setRealtimeReason('Voice realtime room join was rejected');
      }
    });

    if (hasOrganizationVoiceSubscriber()) {
      socket.emit('organization.voice.subscribe', (response?: { ok?: boolean }) => {
        if (response && response.ok === false) {
          setRealtimeReason('Organization voice realtime room join was rejected');
        }
      });
    }

    for (const conversationId of activeConversationIds()) {
      socket.emit('contact_center.conversation.join', { conversationId }, (response?: { ok?: boolean }) => {
        if (response && response.ok === false) {
          setRealtimeReason('Conversation realtime room join was rejected');
        }
      });
    }
  }, [activeConversationIds, activeInboxSubscribers, hasOrganizationVoiceSubscriber, setRealtimeReason]);

  const runCatchUp = useCallback(async () => {
    const startedAt = Date.now();
    let pageCount = 0;
    let eventCount = 0;
    let after = lastCursorRef.current;
    try {
      if (!after) {
        recoverVoiceSubscribers('reconnect_catch_up');
        logger.info('sparkone.realtime.catch_up_observed', {
          outcome: 'authoritative_voice_refresh',
          durationMs: Math.max(0, Date.now() - startedAt),
          pageCount,
          eventCount,
        });
        return;
      }

      for (let page = 0; page < 20; page += 1) {
        const result = await listContactCenterRealtimeEvents({ after, limit: catchUpPageSize });
        pageCount += 1;
        if (result.error) {
          throw new Error(apiErrorMessage(result.error, 'Realtime catch-up failed'));
        }

        const events = result.data?.events ?? [];
        eventCount += events.length;
        for (const event of events) {
          lastCursorRef.current = event.cursor;
          const payload = toRealtimePayload(event);
          if (payload) {
            refreshSubscribers(payload, 'reconnect_catch_up');
          }
        }

        after = result.data?.latestCursor ?? after;
        lastCursorRef.current = after;

        if (!result.data?.hasMore) {
          recoverVoiceSubscribers('reconnect_catch_up');
          logger.info('sparkone.realtime.catch_up_observed', {
            outcome: 'success',
            durationMs: Math.max(0, Date.now() - startedAt),
            pageCount,
            eventCount,
          });
          return;
        }
      }

      throw new Error('Realtime catch-up did not finish within the page limit');
    } catch (error) {
      logger.warn('sparkone.realtime.catch_up_observed', {
        outcome: 'failed',
        durationMs: Math.max(0, Date.now() - startedAt),
        pageCount,
        eventCount,
      });
      throw error;
    }
  }, [recoverVoiceSubscribers, refreshSubscribers]);

  const prepareAndConnect = useCallback(async () => {
    if (
      authTerminatedRef.current ||
      connectingRef.current ||
      (subscribersRef.current.size === 0 && voiceSubscribersRef.current.size === 0)
    ) {
      return;
    }

    const socket = socketRef.current ?? createContactCenterRealtimeSocket();
    socketRef.current = socket;
    connectingRef.current = true;
    updateState({ status: hasConnectedRef.current ? 'reconnecting' : 'connecting', reason: stateRef.current.reason });

    try {
      const selection = await selectPreferredTenant();
      if (authTerminatedRef.current) return;
      if (subscribersRef.current.size === 0 && voiceSubscribersRef.current.size === 0) {
        return;
      }

      if (selection.status !== 'selected') {
        updateState({ status: 'degraded', reason: `Realtime auth preparation failed: ${selection.status}` });
        startFallbackRefresh();
        logger.warn('Contact Center realtime auth preparation failed', { reason: selection.status });
        return;
      }

      setSocketAuth(socket, getBearerToken());
      intentionalDisconnectRef.current = false;
      socket.connect();
    } catch (error) {
      updateState({ status: 'degraded', reason: errorReason(error, 'Realtime auth preparation failed') });
      startFallbackRefresh();
      logger.error('Contact Center realtime auth preparation failed', error);
    } finally {
      connectingRef.current = false;
    }
  }, [startFallbackRefresh, updateState]);

  const scheduleAuthRecovery = useCallback(() => {
    if (authTerminatedRef.current) return;
    if (authRecoveryTimeoutRef.current !== null) {
      return;
    }

    if (authRecoveryAttemptsRef.current >= maxAuthRecoveryAttempts) {
      updateState({ status: 'degraded', reason: 'Realtime auth recovery limit reached' });
      startFallbackRefresh();
      logger.error('Contact Center realtime auth recovery exhausted', undefined, {
        attempts: authRecoveryAttemptsRef.current,
      });
      return;
    }

    authRecoveryAttemptsRef.current += 1;
    const delay = Math.min(5_000, authRecoveryAttemptsRef.current * 1_000);
    logger.info('Contact Center realtime auth recovery scheduled', {
      attempt: authRecoveryAttemptsRef.current,
      delayMs: delay,
    });
    authRecoveryTimeoutRef.current = window.setTimeout(() => {
      authRecoveryTimeoutRef.current = null;
      void prepareAndConnect();
    }, delay);
  }, [prepareAndConnect, startFallbackRefresh, updateState]);

  const ensureSocket = useCallback(() => {
    if (socketRef.current) {
      return socketRef.current;
    }

    const socket = createContactCenterRealtimeSocket();
    socketRef.current = socket;

    const handleConversationEvent = (payload: ContactCenterRealtimePayload) => {
      lastCursorRef.current = payload.cursor;
      refreshSubscribers(payload, 'event');
    };

    const handleInboxEvent = (payload: ContactCenterRealtimeInboxPayload) => {
      lastCursorRef.current = payload.cursor;
      for (const subscriber of subscribersRef.current.values()) {
        if (!subscriber.conversationId) {
          scheduleRefresh(subscriber, undefined, 'event');
        }
      }
    };

    const handleVoiceCallChanged = (payload: ContactCenterVoiceCallChangedPayload) => {
      const current = latestVoiceCallsRef.current.get(payload.callSessionId);
      if (
        (!current && payload.stateSequence > 1) ||
        (current && payload.stateSequence > current.stateSequence + 1)
      ) {
        const previousSequence = current?.stateSequence ?? 0;
        logger.warn('sparkone.realtime.voice_sequence_gap', {
          previousSequence,
          currentSequence: payload.stateSequence,
          gapSize: Math.max(0, payload.stateSequence - previousSequence - 1),
          initialProjection: !current,
        });
        recoverVoiceSubscribers('event');
      }
      let effectivePayload = payload;
      if (shouldAdoptVoiceCall(current, payload)) {
        effectivePayload = reconcileLatestVoiceCall(current, payload);
        latestVoiceCallsRef.current.set(payload.callSessionId, effectivePayload);
      }
      for (const subscriber of voiceSubscribersRef.current.values()) {
        try {
          subscriber.onCallChanged?.(effectivePayload);
        } catch (error) {
          logger.warn('Voice realtime subscriber threw', {
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }
    };

    const handleBrowserRegistrationChanged = (
      payload: ContactCenterBrowserRegistrationChangedPayload,
    ) => {
      const previousVersion =
        latestBrowserRegistrationVersionsRef.current.get(
          payload.installationId,
        );
      // Installation stateVersion is a monotonic stale-event fence, not a
      // contiguous realtime sequence. Internal lifecycle mutations increment
      // it without emitting a Browser registration milestone for every value.
      if (
        previousVersion !== undefined &&
        payload.stateVersion <= previousVersion
      )
        return;
      latestBrowserRegistrationVersionsRef.current.set(
        payload.installationId,
        payload.stateVersion,
      );
      for (const subscriber of voiceSubscribersRef.current.values()) {
        try {
          subscriber.onBrowserRegistrationChanged?.(payload);
        } catch (error) {
          logger.warn('Browser registration realtime subscriber threw', {
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }
    };

    const handleEmployeeVoiceChanged = (payload: EmployeeVoiceChangedPayload) => {
      for (const subscriber of voiceSubscribersRef.current.values()) {
        try { subscriber.onEmployeeVoiceChanged?.(payload); }
        catch (error) { logger.warn('Employee voice realtime subscriber threw', { error: error instanceof Error ? error.message : String(error) }); }
      }
    };

    for (const eventName of conversationEvents) {
      socket.on(eventName, handleConversationEvent);
    }
    socket.on(ContactCenterRealtimeEvent.InboxChanged, handleInboxEvent);
    socket.on(ContactCenterRealtimeEvent.VoiceCallChanged, handleVoiceCallChanged);
    socket.on(
      ContactCenterRealtimeEvent.BrowserRegistrationChanged,
      handleBrowserRegistrationChanged,
    );
    socket.on(ContactCenterRealtimeEvent.EmployeeVoiceChanged, handleEmployeeVoiceChanged);

    socket.on('connect', () => {
      if (authTerminatedRef.current) {
        intentionalDisconnectRef.current = true;
        socket.disconnect();
        return;
      }
      const reconnecting = hasConnectedRef.current;
      const shouldCatchUp = reconnecting && Boolean(lastCursorRef.current);
      hasConnectedRef.current = true;
      authRecoveryAttemptsRef.current = 0;
      clearAuthRecoveryTimeout();
      stopFallbackRefresh();
      syncRooms();
      logger.info('Contact Center realtime connected', {
        recovered: socket.recovered,
        transport: socketTransport(socket),
      });

      if (!shouldCatchUp) {
        if (reconnecting) recoverVoiceSubscribers('reconnect_catch_up');
        updateState(connectedState);
        return;
      }

      updateState({ status: 'reconnecting', reason: 'Catching up missed realtime events' });
      void runCatchUp()
        .then(() => {
          if (
            subscribersRef.current.size > 0 ||
            voiceSubscribersRef.current.size > 0
          ) {
            updateState(connectedState);
          }
        })
        .catch((error: unknown) => {
          updateState({ status: 'degraded', reason: errorReason(error, 'Realtime catch-up failed') });
          startFallbackRefresh();
          logger.error('Contact Center realtime catch-up failed', error);
        });
    });

    socket.on('disconnect', (reason: string) => {
      if (
        intentionalDisconnectRef.current ||
        (subscribersRef.current.size === 0 && voiceSubscribersRef.current.size === 0)
      ) {
        return;
      }

      updateState({ status: 'reconnecting', reason });
      startFallbackRefresh();
      if (reason === 'io server disconnect') {
        // Socket.IO deliberately does not reconnect after an explicit server
        // disconnect. Treat it as a rejected session and re-run the bounded
        // tenant/token preparation path.
        scheduleAuthRecovery();
      }
    });

    socket.on('connect_error', (error: Error) => {
      const reason = errorReason(error, 'Realtime connection failed');
      const code = connectErrorCode(error);
      startFallbackRefresh();
      logger.warn('Contact Center realtime connection failed', { reason, code });

      if (code === 'CONTACT_CENTER_FORBIDDEN') {
        clearAuthRecoveryTimeout();
        updateState({ status: 'degraded', reason });
        return;
      }

      updateState({
        status: hasConnectedRef.current ? 'reconnecting' : 'connecting',
        reason,
      });

      // Authentication middleware rejections are not automatically retried by
      // Socket.IO. All other connect errors belong exclusively to the Manager's
      // transport retry loop.
      if (code && recoverableAuthErrorCodes.has(code)) {
        scheduleAuthRecovery();
      }
    });

    socket.io.on('reconnect_attempt', (attempt: number) => {
      if (authTerminatedRef.current) {
        intentionalDisconnectRef.current = true;
        socket.disconnect();
        return;
      }
      updateState({ status: 'reconnecting', reason: stateRef.current.reason });
      startFallbackRefresh();
      logger.info('Contact Center realtime transport recovery attempted', {
        attempt,
        transport: socketTransport(socket),
      });
    });

    socket.io.on('reconnect_failed', () => {
      if (
        subscribersRef.current.size === 0 &&
        voiceSubscribersRef.current.size === 0
      ) {
        return;
      }
      updateState({ status: 'degraded', reason: 'Realtime reconnection failed' });
      startFallbackRefresh();
      logger.error('Contact Center realtime reconnection failed', undefined, {
        transport: socketTransport(socket),
      });
    });

    return socket;
  }, [clearAuthRecoveryTimeout, recoverVoiceSubscribers, refreshSubscribers, runCatchUp, scheduleAuthRecovery, scheduleRefresh, startFallbackRefresh, stopFallbackRefresh, syncRooms, updateState]);

  const subscribe = useCallback((input: Pick<Subscriber, 'conversationId' | 'onRefresh'>) => {
    clearDisconnectTimeout();
    const subscriber: Subscriber = {
      id: nextSubscriberIdRef.current,
      conversationId: input.conversationId,
      onRefresh: input.onRefresh,
      refreshTimeout: null,
    };
    nextSubscriberIdRef.current += 1;
    subscribersRef.current.set(subscriber.id, subscriber);

    const socket = ensureSocket();
    if (socket.connected) {
      syncRooms();
    } else {
      void prepareAndConnect();
    }

    return () => {
      if (subscriber.refreshTimeout !== null) {
        window.clearTimeout(subscriber.refreshTimeout);
      }
      subscribersRef.current.delete(subscriber.id);

      const activeSocket = socketRef.current;
      if (activeSocket?.connected) {
        if (subscriber.conversationId && !activeConversationIds().has(subscriber.conversationId)) {
          activeSocket.emit('contact_center.conversation.leave', { conversationId: subscriber.conversationId });
        }
        if (!subscriber.conversationId && !activeInboxSubscribers()) {
          activeSocket.emit('contact_center.inbox.unsubscribe');
        }
      }

      if (subscribersRef.current.size === 0 && voiceSubscribersRef.current.size === 0) {
        disconnectWhenIdle();
      }
    };
  }, [activeConversationIds, activeInboxSubscribers, clearDisconnectTimeout, disconnectWhenIdle, ensureSocket, prepareAndConnect, syncRooms]);

  const subscribeVoice = useCallback((
    input: Pick<
      VoiceSubscriber,
      | 'onCallChanged'
      | 'onBrowserRegistrationChanged'
      | 'onEmployeeVoiceChanged'
      | 'onRecover'
    > & { includeOrganization?: boolean },
  ) => {
    clearDisconnectTimeout();
    const subscriber: VoiceSubscriber = {
      id: nextVoiceSubscriberIdRef.current,
      onCallChanged: input.onCallChanged,
      onBrowserRegistrationChanged: input.onBrowserRegistrationChanged,
      onEmployeeVoiceChanged: input.onEmployeeVoiceChanged,
      onRecover: input.onRecover,
      includeOrganization: input.includeOrganization === true,
    };
    nextVoiceSubscriberIdRef.current += 1;
    voiceSubscribersRef.current.set(subscriber.id, subscriber);

    const socket = ensureSocket();
    if (socket.connected) {
      syncRooms();
    } else {
      void prepareAndConnect();
    }

    return () => {
      voiceSubscribersRef.current.delete(subscriber.id);
      const socket = socketRef.current;
      if (voiceSubscribersRef.current.size === 0 && socket?.connected) {
        socket.emit('contact_center.voice.unsubscribe');
      }
      if (subscribersRef.current.size === 0 && voiceSubscribersRef.current.size === 0) {
        disconnectWhenIdle();
      }
    };
  }, [clearDisconnectTimeout, disconnectWhenIdle, ensureSocket, prepareAndConnect, syncRooms]);

  const getVoiceCalls = useCallback((connectorId: string, clientUserRef: string) =>
    Array.from(latestVoiceCallsRef.current.values()).filter((call) =>
      call.connectorId === connectorId &&
      call.clientUserRef === clientUserRef &&
      !["ENDED", "FAILED"].includes(call.state),
    ), []);

  const getLatestVoiceCall = useCallback(
    (connectorId: string, clientUserRef: string) => getVoiceCalls(connectorId, clientUserRef)
      .sort((left, right) => Date.parse(right.lastEventAt) - Date.parse(left.lastEventAt))[0] ?? null,
    [getVoiceCalls],
  );

  // Voice is global operator state: keep its room active for the lifetime of
  // the contact-center shell, including while the VoicePanel route is absent.
  useEffect(() => {
    void prepareAndConnect();
  }, [prepareAndConnect]);

  useEffect(() => {
    const stopAuthenticatedRealtime = () => {
      authTerminatedRef.current = true;
      connectingRef.current = false;
      clearAuthRecoveryTimeout();
      clearDisconnectTimeout();
      stopFallbackRefresh();
      for (const subscriber of subscribersRef.current.values()) {
        if (subscriber.refreshTimeout !== null) {
          window.clearTimeout(subscriber.refreshTimeout);
          subscriber.refreshTimeout = null;
        }
        subscriber.pendingPayload = undefined;
      }
      intentionalDisconnectRef.current = true;
      const socket = socketRef.current;
      if (socket) setSocketAuth(socket, null);
      socket?.disconnect();
      updateState({ status: 'degraded', reason: 'Authentication ended' });
    };
    window.addEventListener('sparkone:auth-lost', stopAuthenticatedRealtime);
    return () => window.removeEventListener('sparkone:auth-lost', stopAuthenticatedRealtime);
  }, [clearAuthRecoveryTimeout, clearDisconnectTimeout, stopFallbackRefresh, updateState]);

  useEffect(() => () => {
    clearDisconnectTimeout();
    clearAuthRecoveryTimeout();
    stopFallbackRefresh();
    for (const subscriber of subscribersRef.current.values()) {
      if (subscriber.refreshTimeout !== null) {
        window.clearTimeout(subscriber.refreshTimeout);
      }
    }
    socketRef.current?.disconnect();
  }, [clearAuthRecoveryTimeout, clearDisconnectTimeout, stopFallbackRefresh]);

  const value = useMemo<ContactCenterRealtimeContextValue>(() => ({
    status: state.status,
    reason: state.reason,
    subscribe,
    subscribeVoice,
    getLatestVoiceCall,
    getVoiceCalls,
  }), [getLatestVoiceCall, getVoiceCalls, state.reason, state.status, subscribe, subscribeVoice]);

  return <ContactCenterRealtimeContext.Provider value={value}><BrowserVoiceRuntimeProvider subscribeVoice={subscribeVoice} persistentEnabled={persistentBrowserVoice}>{children}</BrowserVoiceRuntimeProvider></ContactCenterRealtimeContext.Provider>;
}

export function useContactCenterRealtimeContext(): ContactCenterRealtimeContextValue {
  const context = useContext(ContactCenterRealtimeContext);
  if (!context) {
    throw new Error('ContactCenterRealtimeProvider is required');
  }
  return context;
}

export function useOptionalContactCenterRealtimeContext(): ContactCenterRealtimeContextValue | null {
  return useContext(ContactCenterRealtimeContext);
}
